import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { LoginRequest, LoginResponse, RegisterRequest, Tenant, User } from '@cyphex/types';
import { setWorkspaceSlug } from '@/lib/api/authStorage';
/**
 * Deep import: `@/features/files` is a lazy route chunk, and its barrel would
 * drag the whole Drive page into the boot bundle.
 */
import { ensureContentSession } from '@/features/files/lib/contentSession';
import { refreshAccessToken } from '@/lib/api/refresh';
import { onAccessTokenChange, setAccessToken } from '@/lib/api/token';
import { authApi, type MfaVerifyRequest } from '../api';
import { AuthContext, type AuthContextValue, type AuthStatus } from './authContext';

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Owns the session. Exactly one place turns "the API said yes" into app state,
 * so a token can never be live while the UI thinks it is signed out.
 *
 * COLD START: the access token is memory-only, so a reload has none. What it
 * does have is the httpOnly refresh cookie, so boot = one rotation attempt.
 * Success means we were already signed in; failure means show the sign-in page.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<AuthStatus>('checking');
  const [user, setUser] = useState<User | null>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const mounted = useRef(true);

  /**
   * Turns a login response into app state.
   *
   * The file-content ticket is minted HERE, before `status` flips, and the flip
   * waits for it. Everything the browser fetches on its own — every avatar,
   * every inline attachment, every voicemail player — authorises with that
   * cookie and cannot carry a bearer token, so a view that paints first sends
   * requests that are certain to 401 and does not retry them. The mint needs the
   * access token, hence the ordering; it can never hang the sign-in, because
   * `ensureContentSession` resolves on failure and on a timeout.
   *
   * Skipped for a session held at the password-change gate: that token is scoped
   * to the remediation endpoints, and a ticket is not scoped at all.
   */
  const adopt = useCallback(async (session: LoginResponse) => {
    setAccessToken(session.tokens.accessToken);
    setWorkspaceSlug(session.tenant.slug);
    const mustChange = session.mustChangePassword === true;
    if (!mustChange) await ensureContentSession();
    setUser(session.user);
    setTenant(session.tenant);
    setMustChangePassword(mustChange);
    setStatus('authenticated');
  }, []);

  /* ---- Cold start ------------------------------------------------------- */
  useEffect(() => {
    mounted.current = true;
    void (async () => {
      const token = await refreshAccessToken();
      if (!mounted.current) return;
      if (token === null) {
        setStatus('anonymous');
        return;
      }
      try {
        const viewer = await authApi.viewer();
        if (!mounted.current) return;
        // A reload has no login response left, so remediation state is re-read
        // here. Without it a refreshed tab would render the full shell and take
        // a 403 on every panel it tried to fill.
        const mustChange = viewer.mustChangePassword === true;
        // Same reason as `adopt`: the ticket has to be in the jar before this
        // status flip lets the authenticated tree render. The cookie usually
        // survives the reload, but it expires on its own 12h clock and a tab
        // reopened after that would paint a wall of broken avatars.
        if (!mustChange) await ensureContentSession();
        if (!mounted.current) return;
        setUser(viewer.user);
        setTenant(viewer.tenant);
        setWorkspaceSlug(viewer.tenant.slug);
        setMustChangePassword(mustChange);
        setStatus('authenticated');
      } catch {
        if (!mounted.current) return;
        setAccessToken(null);
        setStatus('anonymous');
      }
    })();
    return () => {
      mounted.current = false;
    };
  }, []);

  /* ---- The token store is the single source of truth -------------------- */
  useEffect(
    () =>
      onAccessTokenChange((token) => {
        if (token !== null || !mounted.current) return;
        // A refresh that could not be rotated dropped the token. Whoever caused
        // it, the session is over — drop the cached tenant data with it.
        setUser(null);
        setTenant(null);
        setMustChangePassword(false);
        setStatus('anonymous');
        queryClient.clear();
      }),
    [queryClient],
  );

  const signIn = useCallback(
    async (input: LoginRequest) => {
      await adopt(await authApi.login(input));
    },
    [adopt],
  );

  const completeMfa = useCallback(
    async (input: MfaVerifyRequest) => {
      await adopt(await authApi.verifyMfa(input));
    },
    [adopt],
  );

  const signUp = useCallback(
    async (input: RegisterRequest) => {
      await adopt(await authApi.register(input));
    },
    [adopt],
  );

  const signOut = useCallback(async () => {
    let endSessionUrl: string | null = null;
    try {
      endSessionUrl = (await authApi.logout())?.endSessionUrl ?? null;
    } finally {
      // Local state is cleared even if the round trip failed: the user asked to
      // be signed out, and the access token dies with the tab either way.
      setAccessToken(null);
      setMustChangePassword(false);
      setStatus('anonymous');
    }

    // SINGLE LOGOUT. A federated session is only half ended when Cyphex forgets
    // it — the identity provider still holds one, and on a shared machine that
    // signs the NEXT person in as this one. The navigation is the last thing
    // done, after local state is already clean, so a blocked or failed
    // navigation still leaves the app properly signed out.
    if (endSessionUrl !== null) window.location.assign(endSessionUrl);
  }, []);

  /**
   * Leaves remediation mode. The API revokes every OTHER session on a password
   * change and re-mints this one without the restriction, so the cached queries
   * collected while the shell was blocked are stale by definition.
   */
  const passwordChanged = useCallback(() => {
    setMustChangePassword(false);
    queryClient.clear();
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      tenant,
      mustChangePassword,
      signIn,
      completeMfa,
      signUp,
      signOut,
      adoptSession: adopt,
      passwordChanged,
    }),
    [
      status,
      user,
      tenant,
      mustChangePassword,
      signIn,
      completeMfa,
      signUp,
      signOut,
      adopt,
      passwordChanged,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
